import { formatPriceValue } from "./lightweightChartUtils";
import { getChartPalette } from "./chartPalette";
import { toTradeNumber } from "./chartOverlayUtils";

export const resetOhlcBox = (element) => {
  if (!element) return;
  element.innerHTML =
    '<span>O --</span><span>H --</span><span>L --</span><span>C --</span><span>--</span>';
};

export const updateOhlcBox = (element, candle, assetType) => {
  if (!element) return;
  if (!candle || !Number.isFinite(candle.open)) {
    resetOhlcBox(element);
    return;
  }

  const change = Number(candle.close) - Number(candle.open);
  const changePct =
    Number(candle.open) !== 0 ? change / Number(candle.open) : null;
  const palette = getChartPalette();
  const changeColor = change >= 0 ? palette.up : palette.down;
  const changeText =
    changePct == null
      ? "--"
      : `${changePct >= 0 ? "+" : ""}${(changePct * 100).toFixed(2)}%`;

  element.innerHTML =
    `<span>O ${formatPriceValue(candle.open, assetType)}</span>` +
    `<span>H ${formatPriceValue(candle.high, assetType)}</span>` +
    `<span>L ${formatPriceValue(candle.low, assetType)}</span>` +
    `<span>C ${formatPriceValue(candle.close, assetType)}</span>` +
    `<span style="color:${changeColor}">${changeText}</span>`;
};

const INTRADAY_SCALE_SECONDS = {
  "1m": 60,
  "2m": 2 * 60,
  "3m": 3 * 60,
  "5m": 5 * 60,
  "10m": 10 * 60,
  "15m": 15 * 60,
  "30m": 30 * 60,
  "1h": 60 * 60,
  "2h": 2 * 60 * 60,
  "3h": 3 * 60 * 60,
  "4h": 4 * 60 * 60,
};

const nowInChartWallClockMs = () => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const parts = formatter.formatToParts(new Date());
  const get = (type) => parts.find((part) => part.type === type)?.value || "0";
  return Date.UTC(
    Number(get("year")),
    Number(get("month")) - 1,
    Number(get("day")),
    Number(get("hour")),
    Number(get("minute")),
    Number(get("second")),
    0
  );
};

const formatCountdownText = (remainingSeconds) => {
  const total = Math.max(0, Math.floor(remainingSeconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

export const hideLivePriceLabel = (element) => {
  if (!element) return;
  element.style.display = "none";
};

const resolveCountdownState = ({ scale, latestCandle }) => {
  if (!latestCandle || !Number.isFinite(latestCandle.time)) return null;

  const bucketSeconds = INTRADAY_SCALE_SECONDS[scale];
  if (!Number.isFinite(bucketSeconds) || bucketSeconds <= 0) return null;

  const nowCandidates = [
    Math.floor(Date.now() / 1000),
    Math.floor(nowInChartWallClockMs() / 1000),
  ];

  const candidates = nowCandidates
    .map((nowSeconds) => {
      const bucketStart = Math.floor(nowSeconds / bucketSeconds) * bucketSeconds;
      return {
        nowSeconds,
        bucketStart,
        diff: Math.abs(latestCandle.time - bucketStart),
      };
    })
    .sort((a, b) => a.diff - b.diff);

  const best = candidates[0];
  if (!best || best.diff > bucketSeconds * 2) return null;

  return {
    text: formatCountdownText(best.bucketStart + bucketSeconds - best.nowSeconds),
  };
};

export const updateLivePriceLabel = ({
  element,
  chart,
  series,
  latestCandle,
  scale,
  assetType,
  isMobile,
  extendedHoursQuote = null,
}) => {
  if (!element || !chart || !series || !latestCandle) {
    hideLivePriceLabel(element);
    return;
  }

  const extendedPrice = toTradeNumber(extendedHoursQuote?.price);
  const referencePrice =
    toTradeNumber(extendedHoursQuote?.referencePrice) ??
    toTradeNumber(latestCandle.open);
  const displayPrice = extendedPrice ?? latestCandle.close;
  const countdown = extendedPrice == null
    ? resolveCountdownState({ scale, latestCandle })
    : null;
  const yCoordinate = series.priceToCoordinate?.(displayPrice);
  const containerHeight = chart?.options?.()?.height || element.parentElement?.clientHeight || 0;
  if (!Number.isFinite(yCoordinate) || !Number.isFinite(containerHeight) || containerHeight <= 0) {
    hideLivePriceLabel(element);
    return;
  }

  const palette = getChartPalette();
  const isUp = Number(displayPrice) >= Number(referencePrice);
  const isExtendedHours = extendedPrice != null;
  const backgroundColor = isExtendedHours
    ? palette.extendedHours
    : isUp
      ? palette.up
      : palette.down;
  const priceText = formatPriceValue(displayPrice, assetType);
  const detailText = extendedPrice != null
    ? extendedHoursQuote?.label || "Extended hours"
    : countdown?.text || "";

  element.innerHTML =
    `<span style="font-size:${isMobile ? "10px" : "11px"};font-weight:600;line-height:1;">${priceText}</span>` +
    (detailText
      ? `<span style="font-size:${isMobile ? "9px" : "10px"};font-weight:500;line-height:1.05;opacity:0.9;">${detailText}</span>`
      : "");

  element.style.display = "flex";
  element.style.background = backgroundColor;
  element.style.color = isExtendedHours ? palette.extendedHoursText : "#ffffff";
  element.style.flexDirection = "column";
  element.style.alignItems = "flex-start";
  element.style.gap = detailText ? "2px" : "0px";

  const labelHeight = Math.max(element.offsetHeight || (detailText ? 32 : 22), 22);
  const halfHeight = labelHeight / 2;
  const clampedCenter = Math.max(halfHeight + 2, Math.min(yCoordinate, containerHeight - halfHeight - 2));
  element.style.top = `${clampedCenter}px`;
};
